import * as turf from '@turf/turf';

import { Bounds, EventsFilter, MapboxBounds } from '../../interfaces';
import { deepCopy, deepEqual } from '../../utils';

export const getBoundsFromMapboxBounds = (mapboxBounds: MapboxBounds): Bounds => {
  return {
    ne: { ...mapboxBounds._ne },
    nw: { lat: mapboxBounds._sw.lat, lng: mapboxBounds._ne.lng },
    sw: { ...mapboxBounds._sw },
    se: { lat: mapboxBounds._ne.lat, lng: mapboxBounds._sw.lng },
  };
};

export const getBoundsFromGoogleAutocomplete = (googleMapGeocoderResult: any): Bounds => {
  const distanceForBounds = 50;
  const northEastBearing = 45;
  const southWestBearing = -135;
  const geometry = googleMapGeocoderResult?.geometry;
  if (!geometry || (!geometry.location && !geometry.bounds)) {
    return null;
  }
  const { location, bounds } = googleMapGeocoderResult?.geometry;
  let northeast;
  let southwest;
  if (bounds) {
    northeast = bounds.northeast;
    southwest = bounds.southwest;
  } else if (location) {
    const { lat, lng } = location;
    northeast = turf.destination([lat, lng], distanceForBounds, northEastBearing);
    southwest = turf.destination([lat, lng], distanceForBounds, southWestBearing);
  }

  // sometimes we get a geojson here
  if (northeast?.geometry?.coordinates?.[0]) {
    return {
      ne: { lat: northeast.geometry.coordinates[0], lng: northeast.geometry.coordinates[1] },
      nw: { lat: southwest.geometry.coordinates[0], lng: northeast.geometry.coordinates[1] },
      sw: { lat: southwest.geometry.coordinates[0], lng: southwest.geometry.coordinates[1] },
      se: { lat: northeast.geometry.coordinates[0], lng: southwest.geometry.coordinates[1] },
    };
  }

  return {
    ne: { ...northeast },
    nw: { lat: southwest.lat, lng: northeast.lng },
    sw: { ...southwest },
    se: { lat: northeast.lat, lng: southwest.lng },
  };
};

export const boundsToMapboxBounds = (bounds: Bounds): number[][] => {
  return [
    [bounds.sw.lng, bounds.sw.lat],
    [bounds.ne.lng, bounds.ne.lat],
  ];
};

/**
 * Checks if filter without bounds has changed
 */
export const isFilterChanged = (filter: EventsFilter, prevFilter: EventsFilter) => {
  if (!prevFilter) {
    return false;
  }
  const _prevFilter = deepCopy(prevFilter);
  delete _prevFilter.bounds;
  const _filter = deepCopy(filter);
  delete _filter.bounds;

  return !deepEqual(_prevFilter, _filter);
};

export const locationAppeared = (filter: EventsFilter, prevFilter: EventsFilter) => {
  if (!prevFilter) {
    return false;
  }
  return !prevFilter.location && filter.location;
};
